import React, { FC } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Toggler from 'modules/core/components/Toggler'
import { dateToString } from 'modules/core/lib/dates'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { translations as datesStepTranslations } from 'modules/contracts/components/FixFlow/lib/datesTranslations'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import { useContractVersions } from 'modules/contracts/lib/contractVersionsContext'
import { getFirstError } from 'modules/contracts/lib/errors'
import { translate } from 'modules/contracts/lib/translate'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

interface Props {
  form: Form
  translations?: { [key: string]: string }
  type?: ContractFlowType
  contractVersion?: ContractVersion
}

interface DateTooltipProps {
  translations: { [key: string]: string }
}

const EffectiveDateTooltip = ({ translations }: DateTooltipProps) => {
  return (
    <InformationTooltip
      information={translate(translations, 'effective_date_tooltip')}
      icon="info"
    />
  )
}

const StartDateTooltip = ({ translations }: DateTooltipProps) => {
  return (
    <InformationTooltip information={translate(translations, 'start_date_tooltip')} icon="info" />
  )
}

const getPreviousContractVersion = (
  effectiveOn: string | null,
  contractVersions: ContractVersion[],
  contractVersion: ContractVersion | undefined
) => {
  if (!effectiveOn) return undefined
  const effectiveOnDate = stringToDate(effectiveOn, 'ui')
  const filteredContractVersions = contractVersions.filter((cv) => cv.id !== contractVersion?.id)
  const sortedContractVersions = orderBy(filteredContractVersions, ['effectiveOn'], ['desc'])

  return sortedContractVersions.find((cv) => cv.effectiveOn.isBefore(effectiveOnDate))
}

const ContractDates: FC<Props> = ({
  form,
  type = 'fix',
  contractVersion,
  translations = datesStepTranslations(), // Setting fix flow translation so as to avoid passing this from task detail page
}) => {
  const effectiveOnField = form.get('effective_on')
  const startsOnField = form.has('starts_on') ? form.get('starts_on') : undefined
  const contractVersions = useContractVersions()
  const isFixFlow = type === 'fix'

  const previousContractVersion = getPreviousContractVersion(
    effectiveOnField.value,
    contractVersions.models,
    contractVersion
  )

  const shouldShowOverlappingSwitch = (effectiveOn: string | null) => {
    return hasOverlapping(effectiveOn) && type === 'update'
  }

  /**
   * There are some cases where we need to ask the user if wants to create a new contract or a new version.
   * It happens when there is an overlapping between the previous version and the new effecitveOn date.
   *
   * Possible use case: an intership that is hired the day after the practices contract expires.
   * If we don't ask, we treat this as a new version, but now the user is able to tell us that is a new contract
   * what they need.
   */
  const hasOverlapping = (effectiveOnValue: string | null): boolean => {
    if (!effectiveOnValue) return false
    const effectiveOnDate = stringToDate(effectiveOnValue, 'ui')

    const previousVersion = getPreviousContractVersion(
      effectiveOnValue,
      contractVersions.models,
      contractVersion
    )

    // This version is the very first. Thus, we know that we need a new start date. No need to ask.
    if (!previousVersion) return false

    const previousVersionEndDate = previousVersion.endsOn
    /**
     * Previous version has no end date. Thus there is overlapping.
     * Need to ask to the user to resolve ambiguity
     */
    if (!previousVersionEndDate) {
      return true
    }
    /**
     * We need to ask just in case there is an overlapping.
     * If effective on is after the ends on date, there is no overlaping.
     * No need to ask as we know we need to create a new contract.
     */
    return previousVersionEndDate.isSameOrAfter(effectiveOnDate)
  }

  const [showOverlappingSwitch, setShowOverlappingSwitch] = React.useState(false)

  const onEffectiveOnChange = (value: string) => {
    setShowOverlappingSwitch(shouldShowOverlappingSwitch(value))
    effectiveOnField.set(value)
  }

  const onStartsOnSwitchToggle = () => {
    if (startsOnField) {
      if (startsOnField.isDirty) startsOnField.set(startsOnField.originalValue)
      else startsOnField.set(effectiveOnField.value)
    }
  }

  const startDateDescription =
    (startsOnField && startsOnField.value) || !previousContractVersion
      ? i18n.t('contract_versions.flows.fix.steps.dates.contract_start_date.new_date_description', {
          startsOn: dateToString(stringToDate(effectiveOnField.value, 'ui'), 'ui'),
        })
      : i18n.t(
          'contract_versions.flows.fix.steps.dates.contract_start_date.current_date_description',
          {
            startsOn: dateToString(
              previousContractVersion.startsOn ?? previousContractVersion.effectiveOn,
              'ui'
            ),
          }
        )

  return (
    <Box gap="s24">
      <DayPicker
        value={effectiveOnField.value}
        error={getFirstError(effectiveOnField)}
        onChange={(value) => onEffectiveOnChange(value)}
        label={translate(translations, 'effective_date')}
        secondaryLabel={<EffectiveDateTooltip translations={translations} />}
        minDate={stringToDate(startsOnField?.value, 'ui')}
        mandatory
      />

      {isFixFlow && startsOnField && (
        <DayPicker
          value={startsOnField.value}
          error={getFirstError(startsOnField!)}
          onChange={(value) => startsOnField.set(value)}
          label={translate(translations, 'start_date')}
          secondaryLabel={<StartDateTooltip translations={translations} />}
        />
      )}

      {showOverlappingSwitch && startsOnField && (
        <Box gap="s16">
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            padding="s16"
            border={{
              all: {
                width: 's2',
                color: 'grey400',
                style: 'solid',
              },
            }}
            borderRadius={{
              all: 'abs008',
            }}
          >
            <Text.Normal color="grey800" size="300" weight="medium">
              {translate(translations, 'new_start_date_title')}
            </Text.Normal>
            <Toggler active={startsOnField.isDirty} onChange={() => onStartsOnSwitchToggle()} />
          </Box>
          <Text.Normal color="grey800" size="200" weight="normal">
            {startDateDescription}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export default observer(ContractDates)
